import * as React from 'react';
import { Button, CircularProgress, Divider, makeStyles, Typography } from '@material-ui/core';
import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useListEpicFacilities } from 'client/hooks/epic/useListEpicFacilities';
import { useGetProtectedObject } from 'common/hooks/storage/useGetProtectedObject';
import { Facility } from 'common/types';
import placeholder from 'img/company-placeholder.png';
import type { Mode } from './Layout';

interface PanelProps {
    onSelect: (facilityId: number) => void;
    onChangeMode: (mode: Mode) => void;
}

interface FacilityListItemProps {
    facility: Facility;
    handleSelect: (facilityId: number) => void;
}

const FacilityListItem = ({ facility, handleSelect }: FacilityListItemProps) => {
    const facilityPhoto = useGetProtectedObject(facility?.photoUrl);
    const facilityAvatar = facility?.photoUrl ? facilityPhoto : placeholder;

    return (
        <ListItemButton key={facility.id} onClick={() => handleSelect(facility.id)}>
            <ListItemAvatar>
                <Avatar src={facilityAvatar} />
            </ListItemAvatar>
            <ListItemText
                primary={facility.name}
                secondary={facility.streetAddress ? `${facility.streetAddress}, ${facility.city}, ${facility.state} ${facility.zip}` : ''}
            />
        </ListItemButton>
    );
};

// Epic standalone SSO initiator facility select panel
export const EpicFacilitySelectPanel: React.FC<PanelProps> = ({ onSelect, onChangeMode }) => {
    const { facilities, isLoading } = useListEpicFacilities();
    const classes = useStyles();

    const handleSelect = (facilityId: number) => onSelect(facilityId);

    return (
        <>
            <Typography component="h2" variant="h6">
                Select Epic Facility
            </Typography>
            <List sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}>
                {isLoading && <CircularProgress size={25} thickness={8} color="primary" />}
                {!isLoading &&
                    facilities &&
                    facilities.map((facility: Facility, index: number) => (
                        <React.Fragment key={index}>
                            {facility.isListed && <FacilityListItem facility={facility} handleSelect={handleSelect} />}
                            {index < facilities.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
            </List>
            <div className={classes.links}>
                <Button size="small" onClick={() => onChangeMode('login')}>
                    Back to Login
                </Button>
            </div>
        </>
    );
};

const useStyles = makeStyles({
    links: {
        display: 'flex',
        justifyContent: 'space-between',
    },
});
